import { Button, Modal, Select, Skeleton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode } from '../personnel/source.js';
import { purchasingError } from './errors.js';
import classes from './Purchasing.module.css';
import { formatAmount } from './requisitions.js';
import type { PurchaseOrderEditor } from './source.js';
import type { PurchaseOrderCandidate, PurchaseOrderDetail } from './types.js';

/**
 * "New PO" (ADR 0040 #179): tedarikçisi seçilmiş ve etkin siparişi olmayan RFQ seçilir; kalemler,
 * fiyatlar ve maliyet kalemleri seçilen tekliften kopyalanır. Teslim bilgileri taslakta düzeltilir.
 */
export function NewPurchaseOrderDialog({
  editor,
  onClose,
  onCreated,
}: {
  editor: PurchaseOrderEditor;
  onClose: () => void;
  onCreated: (order: PurchaseOrderDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.po.new.${key}`, options);
  const [candidates, setCandidates] = useState<PurchaseOrderCandidate[] | null>(null);
  const [rfqId, setRfqId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    editor
      .candidates()
      .then((items) => {
        if (!alive) return;
        setCandidates(items);
        setRfqId(items[0]?.rfqId ?? null);
      })
      .catch((error: unknown) => {
        if (alive) setFailure(errorCode(error));
      });
    return () => {
      alive = false;
    };
  }, [editor]);

  return (
    <Modal
      opened
      onClose={onClose}
      title={k('title')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!rfqId) return;
          setBusy(true);
          try {
            onCreated(await editor.create(rfqId));
          } catch (error) {
            setFailure(errorCode(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={classes.muted}>{k('body')}</p>
        {candidates === null && !failure ? (
          <Skeleton height={56} mb="sm" />
        ) : candidates && candidates.length === 0 ? (
          <p className={classes.notice} data-tone="info">
            {k('none')}
          </p>
        ) : candidates ? (
          <Select
            label={k('rfq')}
            required
            mb="sm"
            allowDeselect={false}
            data={candidates.map((c) => ({
              value: c.rfqId,
              label: k('option', {
                rfq: `${c.rfqNo} (${c.requisitionNo}, ${c.vesselName})`,
                supplier: c.supplierName,
                total: formatAmount(c.total, locale),
                currency: c.currency,
              }),
            }))}
            value={rfqId}
            onChange={setRfqId}
          />
        ) : null}
        {failure ? (
          <p className={classes.error} role="alert">
            {purchasingError(t, failure, 'po')}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy} disabled={!rfqId}>
            {k('create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
